import { Component } from 'opus-angular-seed-core';
import template from './template.html';
import { stripTrailingEmptyParagraphs } from '../../../../../services/htmlContent';
import {
    buildSourceChapters,
    buildSourceSections,
    collectChaptersByHash,
    collectModelChapters,
    createChapterHash,
    createUniqueKey,
    findChapterInModel,
    findSectionByUniqueKey,
    findSectionForTranslation,
    isSameChapter
} from './materialModels';
import {
    doesChapterNeedReview,
    formatTranslationConfirmedAt,
    getChapterTranslationState,
    isChapterUntranslated,
    isTranslationOutdated,
    markChapterTranslationForReview,
    translateWithLanguage
} from './translationState';

@Component({
    bindings: {
        uibModalInstance: '=',
        trainingForm: '=',
        company: '=',
        course: '=',
        context: '=',
        viewMode: '='
    },
    template
})
export default class {
    constructor (TokenService, CourseTrainingResource, lodash, $rootScope, $scope, $document, toastr, $translate,
        COMPANY_LANGUAGE_ENUM, ModalService, CourseVersionTrainingFormMaterialService, $q, PrivilegeService
    ) {
        this.$rootScope = $rootScope;
        this.$scope = $scope;
        this.$q = $q;
        this.TokenService = TokenService;
        this.CourseTrainingResource = CourseTrainingResource;
        this.lodash = lodash;
        this.toastr = toastr;
        this.$translate = $translate;
        this.$document = $document;
        this.COMPANY_LANGUAGE_ENUM = COMPANY_LANGUAGE_ENUM;
        this.ModalService = ModalService;
        this.MaterialService = CourseVersionTrainingFormMaterialService;

        this.companySupportedLanguages = this.TokenService.getCompanySupportedLanguages();
        this.defaultLanguage = this.TokenService.getCompanyLanguage();
        // the company module and the privilege of the user are both needed to use AI translations
        this.isAiTranslationModuleEnabled = this.TokenService.isAiTranslationModuleEnabled();
        this.isAiTranslationAvailable = this.isAiTranslationModuleEnabled
            && PrivilegeService.canUseAiTranslations();

        // for already generated chapters, we will not regenerate tinyMce editor to not waste tokens
        this.chapterUniqueKeysForGeneratedTinyMce = [];

        // a target language is opened with the source language next to it
        this.showSourceComparison = true;

        // Transform unified API format to per-language format before processing
        this.trainingForm.materials = this.MaterialService
            .transformMaterialsFromApi(this.trainingForm.materials);

        // if material for specific language doesn't exist, we add language with flag isEnabled = false
        this.addDefaultMaterials();

        // we need select first enabled, only then default
        if (!this.selectedLanguage) {
            this.selectedLanguage = this.getDefaultSelectedLanguage();
        }

        $scope.$on('authoringTool:toEditor', () => {
            if (this.isEditorViewGenerated) {
                this.viewMode = 'editor';
                return;
            }

            this.uibModalInstance.close();
        });

        this.models = [];

        this.setMaterial();
        this.convertMaterialsToModels();
        this.rememberSavedContents();
        this.initSettings();
        this.keepSourceLanguageVisible();
        this.setActiveChapter();

        this.initMode(this.viewMode);

        this.handleOutsideMenusClicks();
        this.handleSettingsPanelClosing();

        // to be sure models data is generated
        setTimeout(() => {
            this.initialStateHash = this.getStateHash();
        }, 200);

        this.setDynamicTranslations(this.selectedLanguage);

        this.closeModal = this.closeModal.bind(this);
        this.initMode = this.initMode.bind(this);
    }

    setDynamicTranslations (lang) {
        this.$translate.use(lang.toLowerCase()).then(() => {
            this.dynamicTranslations = {
                hello: this.$translate.instant('general.Hello'),
                startNewLanguage: this.$translate.instant('authoringTool.To start with a new language, make a choice on the left')
            };
            this.$translate.use(this.TokenService.getActiveLanguage());
        });
    }

    initMode (mode) {
        this.previewLoading = true;

        if (mode === 'editor') {
            this.isEditorViewGenerated = true;
            this.previewLoading = false;
            this.viewMode = mode;
            return;
        }

        if (mode === 'preview') {
            const selected = this.models[this.materialIndex] ? this.models[this.materialIndex].selected : null;

            // we add timeout only when we change from editor to preview
            // to be sure content from inyMce is updated
            if (this.isEditorViewGenerated) {
                setTimeout(() => {
                    this.$scope.$apply(() => {
                        this.previewMaterial = this.buildPreviewMaterial(selected);
                        this.previewLoading = false;
                        this.viewMode = mode;
                    });
                }, 300);

                return;
            }
            this.previewMaterial = this.buildPreviewMaterial(selected);
            this.previewLoading = false;
            this.viewMode = mode;
        }
    }

    /**
     * Builds the material shown in the preview. Hidden chapters are only meant for the
     * editor, so they are left out of the preview.
     */
    buildPreviewMaterial (selected) {
        const material = this.lodash.cloneDeep(this.trainingForm.materials[this.materialIndex]);
        const model = this.lodash.cloneDeep(this.models[this.materialIndex]);
        const previewMaterial = this.addDataFromModelToMaterial(model, material);

        previewMaterial.chapters = this.lodash.filter(previewMaterial.chapters, chapter => chapter.isVisible !== false);

        if (selected) {
            const initialChapter = previewMaterial.chapters
                .findIndex(chapter => chapter.uniqueKey === selected.uniqueKey);
            previewMaterial.initialChapter = initialChapter === -1 ? 0 : initialChapter;
        }

        return previewMaterial;
    }

    removeMenuEditMode () {
        this.edit = false;
        this.lodash.forEach(this.models[this.materialIndex].list, item => {
            item.edit = false;
            if (item.type === 'section') {
                this.lodash.forEach(item.items, subitem => {
                    subitem.edit = false;
                });
            }
        });
    }

    closeMenus (exceptionItem = null) {
        this.lodash.forEach(this.models[this.materialIndex].list, item => {
            if (exceptionItem && exceptionItem.$$hashKey === item.$$hashKey && item.actions === true) {
                item.actions = true;
            }
            else {
                item.actions = false;
            }
            if (item.type === 'section') {
                this.lodash.forEach(item.items, subitem => {
                    if (exceptionItem && exceptionItem.$$hashKey === subitem.$$hashKey && subitem.actions === true) {
                        subitem.actions = true;
                    }
                    else {
                        subitem.actions = false;
                    }
                });
            }
        });
    }

    /**
     * Opens the menu of an item of the chapter list, only one menu is open at a time.
     */
    toggleItemMenu (item) {
        this.actions = false;
        this.closeMenus(item);
        item.actions = !item.actions;
    }

    toggleAddContentMenu () {
        this.closeMenus();
        this.actions = !this.actions;
    }

    startItemEdit (item) {
        this.closeMenus();
        this.actions = false;
        item.edit = true;
        this.edit = true;
    }

    finishItemEdit (item) {
        item.edit = false;
        this.edit = false;
    }

    getChapters () {
        return collectModelChapters(this.models[this.materialIndex]);
    }

    setChapter (chapter) {
        const model = this.models[this.materialIndex];
        if (!model) {
            return;
        }
        model.selected = chapter;
        this.chapterUniqueKeysForGeneratedTinyMce.push(chapter.uniqueKey);
    }

    initItem (item, type) {
        if (item.uniqueKey === undefined) {
            item.uniqueKey = createUniqueKey();
        }
        item.type = type;
        item.status = 'ok';
        item.actions = false;
        item.effect = 'move';
        item.edit = false;

        if (type === 'section') {
            item.items = [];
            item.allowedTypes = ['chapter'];
            item.$$hashKey = createUniqueKey();
        }

        if (type === 'chapter') {
            if (item.isVisible === undefined) {
                item.isVisible = true;
            }
            // convert chapter's section to sectionData to be able to save later correctly
            // eslint-disable-next-line eqeqeq
            if (item.section != null) {
                item.sectionData = item.section;
                if (item.sectionData.id) {
                    item.sectionData.uniqueKey = item.section.id;
                }
                // eslint-disable-next-line eqeqeq
                if (item.sectionData.uniqueKey == null) {
                    item.sectionData.uniqueKey = createUniqueKey();
                }
                delete item.section;
                delete item.sectionId;
            }
        }

        return item;
    }

    /**
     * models structure:
     * this.models = [
     *      list: [
     *          {
     *              type: 'section',
     *              name: 'section name',
     *              items: [
     *                  {
     *                      type: 'chapter,
     *                      name: 'chapter with section'
     *                  }
     *              ]
     *          },
     *          {
     *              type: 'chapter
     *              name: 'chapter without section'
     *          }
     *      ]
     * ]
     */
    convertMaterialsToModels () {
        // the chapter structure is language neutral, so the same chapter has to carry the
        // same uniqueKey in every language, otherwise structural changes (like deleting a
        // chapter) would only be applied to the language that is currently open
        const uniqueKeysByHash = {};

        // set initial models
        this.lodash.forEach(this.trainingForm.materials, (material, index) => {
            const sections = [];

            if (this.models[index] === undefined) {
                this.models[index] = {
                    list: []
                };
            }
            this.models[index].isEnabled = material.isEnabled;
            this.lodash.forEach(material.chapters, chapter => {
                let modelChapter = this.lodash.clone(chapter);
                if (modelChapter.hash && modelChapter.uniqueKey === undefined) {
                    if (uniqueKeysByHash[modelChapter.hash] === undefined) {
                        uniqueKeysByHash[modelChapter.hash] = createUniqueKey();
                    }
                    modelChapter.uniqueKey = uniqueKeysByHash[modelChapter.hash];
                }
                // add needed initial properties
                modelChapter = this.initItem(modelChapter, 'chapter');

                // chapter with section
                if (modelChapter.sectionData !== undefined) {
                    let section = sections.find(item => item.uniqueKey === modelChapter.sectionData.uniqueKey);
                    if (section === undefined) {
                        section = this.lodash.clone(modelChapter.sectionData);
                        delete modelChapter.sectionData;
                        section = this.initItem(section, 'section');
                        sections.push(section);
                        this.models[index].list.push(section);
                    }
                    section.items.push(this.lodash.clone(modelChapter));
                }
                // chapter without section
                else {
                    this.models[index].list.push(modelChapter);
                }
            });
        });
    }

    initSettings () {
        // settings are shared across all language materials of the same content
        const material = this.lodash.find(this.trainingForm.materials, m => m.settings && m.settings.chapterNavigation);
        const settings = material ? material.settings : {};

        this.chapterNavigation = settings.chapterNavigation ? settings.chapterNavigation : 'free';
        this.automaticTranslationsEnabled = settings.automaticTranslationsEnabled === true;
        this.sourceLanguage = settings.sourceLanguage ? settings.sourceLanguage : this.defaultLanguage;
    }

    convertModelsToMaterials () {
        // the source language may have been changed in the settings
        this.keepSourceLanguageVisible();
        this.lodash.forEach(this.models, (model, materialIndex) => {
            this.trainingForm.materials[materialIndex] = this.addDataFromModelToMaterial(
                model,
                this.trainingForm.materials[materialIndex]
            );
        });
    }

    addDataFromModelToMaterial (model, material) {
        let chapterSequence = 0;
        const chapters = [];
        this.lodash.forEach(model.list, item => {
            if (item.type === 'section') {
                this.lodash.forEach(item.items, chapter => {
                    chapter.sequence = chapterSequence++;
                    chapter.sectionData = {
                        id: item.id,
                        uniqueKey: item.uniqueKey,
                        name: item.name
                    };
                    chapter.section = chapter.sectionData;
                    chapter.isVisible = chapter.isVisible !== false;
                    chapter.isTranslationConfirmed = this.isChapterTranslationConfirmed(chapter, material);
                    chapters.push(chapter);
                });
            }
            if (item.type === 'chapter') {
                item.sequence = chapterSequence++;
                item.isVisible = item.isVisible !== false;
                item.isTranslationConfirmed = this.isChapterTranslationConfirmed(item, material);

                delete item.sectionData;
                delete item.section;
                delete item.sectionId;

                chapters.push(item);
            }
        });
        // Strip trailing blank lines before previewing/saving so they don't accumulate
        // across Preview/Edit cycles (the editor keeps one for typing; it's dropped here).
        this.lodash.forEach(chapters, chapter => {
            if (chapter.content) {
                chapter.content = stripTrailingEmptyParagraphs(chapter.content);
            }
        });

        material.isEnabled = model.isEnabled;
        material.chapters = chapters;
        material.settings = {
            chapterNavigation: this.chapterNavigation,
            automaticTranslationsEnabled: this.automaticTranslationsEnabled,
            sourceLanguage: this.sourceLanguage
        };

        return material;
    }

    /**
     * The source language content is written by hand, so it is never waiting for a translation review.
     */
    isChapterTranslationConfirmed (chapter, material) {
        if (material && material.lang === this.sourceLanguage) {
            return true;
        }

        return chapter.isTranslationConfirmed !== false;
    }

    getSelectedChapter () {
        const model = this.models[this.materialIndex];
        if (!model || !model.selected || model.selected.type !== 'chapter') {
            return null;
        }

        return model.selected;
    }

    /**
     * The comparison and the review queue apply to every target language, no matter whether the
     * translation was generated automatically or written by hand.
     */
    isSourceLanguageSelected () {
        return this.selectedLanguage === this.sourceLanguage;
    }

    /**
     * The source language is an AI translation concept, without the company module it is shown like
     * any other language.
     */
    isSourceLanguageInfoShown () {
        return this.isAiTranslationModuleEnabled && this.isSourceLanguageSelected();
    }

    hasChapters () {
        const model = this.models[this.materialIndex];

        return !!model && !!model.list && model.list.length > 0;
    }

    isSourceComparisonAvailable () {
        return !this.isSourceLanguageSelected() && this.hasChapters() && !!this.getSourceLanguageModel();
    }

    /**
     * Comparing is on by default, hiding it is a preference of the open editor only - it never
     * changes the material itself.
     */
    isSourceComparisonVisible () {
        return this.isSourceComparisonAvailable() && this.showSourceComparison !== false;
    }

    toggleSourceComparison () {
        this.showSourceComparison = !this.isSourceComparisonVisible();
    }

    getSourceComparisonToggleLabel () {
        if (this.isSourceComparisonVisible()) {
            return this.$translate.instant('authoringTool.Hide original');
        }

        return translateWithLanguage(
            this.$translate,
            'authoringTool.Compare with language',
            this.COMPANY_LANGUAGE_ENUM.LABELS[this.sourceLanguage]
        );
    }

    getTargetPaneLabel () {
        return translateWithLanguage(
            this.$translate,
            'authoringTool.Language translation',
            this.COMPANY_LANGUAGE_ENUM.LABELS[this.selectedLanguage]
        );
    }

    /**
     * The source language chapter that matches the selected one, used as a read-only reference.
     *
     * The chapter of the source language model is returned as it is, nothing of it is ever copied
     * into the edited target language chapter.
     */
    getSelectedSourceChapter () {
        const chapter = this.getSelectedChapter();
        const sourceModel = this.getSourceLanguageModel();
        if (!chapter || !sourceModel || sourceModel === this.models[this.materialIndex]) {
            return null;
        }

        return findChapterInModel(sourceModel, chapter.hash);
    }

    /**
     * The translation state of a chapter of the selected target language, one of
     * missing / pending / outdated / current.
     */
    getSelectedLanguageChapterState (chapter) {
        if (!chapter || chapter.type !== 'chapter') {
            return null;
        }

        const sourceModel = this.getSourceLanguageModel();
        const sourceChapter = sourceModel && sourceModel !== this.models[this.materialIndex]
            ? findChapterInModel(sourceModel, chapter.hash)
            : null;

        return getChapterTranslationState(chapter, sourceChapter, this.isSourceChanged(sourceChapter));
    }

    /**
     * Whether the source language chapter was changed in the editor since it was loaded or saved.
     * The saved state of the backend only knows the stored content, so a target language would
     * look up to date until the source change is saved.
     */
    isSourceChanged (sourceChapter) {
        if (!sourceChapter || !this.savedContents) {
            return false;
        }

        const saved = (this.savedContents[this.sourceLanguage] || {})[sourceChapter.hash];
        if (!saved) {
            return false;
        }

        const name = sourceChapter.name || '';
        const content = sourceChapter.content || '';
        if (name === saved.name && content === saved.content) {
            return false;
        }

        // the editor keeps a trailing empty line for typing, it is dropped when the content is saved
        const cached = this.sourceChangeCache[sourceChapter.hash];
        if (cached && cached.name === name && cached.content === content && cached.saved === saved) {
            return cached.isChanged;
        }

        const isChanged = name !== saved.name
            || stripTrailingEmptyParagraphs(content) !== saved.strippedContent;
        this.sourceChangeCache[sourceChapter.hash] = { name, content, saved, isChanged };

        return isChanged;
    }

    /**
     * Remembers the content of every language as it is stored, the base for recognizing a source
     * change that is not saved yet.
     */
    rememberSavedContents () {
        this.savedContents = {};
        this.sourceChangeCache = {};

        this.lodash.forEach(this.trainingForm.materials, (material, index) => {
            const contents = {};
            this.lodash.forEach(collectModelChapters(this.models[index]), chapter => {
                const content = chapter.content || '';
                contents[chapter.hash] = {
                    name: chapter.name || '',
                    content,
                    strippedContent: stripTrailingEmptyParagraphs(content)
                };
            });
            this.savedContents[material.lang] = contents;
        });
    }

    /**
     * The short state of a chapter shown under its name in the chapter list: for the source
     * language how many other languages still wait for a translation, for a target language the
     * translation state of the chapter itself.
     */
    getChapterStateLabel (chapter) {
        if (!chapter || chapter.type !== 'chapter') {
            return null;
        }

        if (this.isSourceLanguageSelected()) {
            const languageCount = this.getChapterPendingTranslationLanguageCount(chapter);

            return languageCount
                ? this.$translate.instant('authoringTool.Languages need translating', { count: languageCount })
                : null;
        }

        if (chapter.isVisible === false) {
            return this.$translate.instant('authoringTool.Hidden in this language');
        }

        const state = this.getSelectedLanguageChapterState(chapter);
        if (state === 'missing') {
            return this.$translate.instant('authoringTool.Not translated');
        }
        if (state === 'pending') {
            return this.$translate.instant('authoringTool.Waiting for your review');
        }
        if (state === 'outdated') {
            return this.$translate.instant('authoringTool.Source has changed');
        }

        return this.$translate.instant('authoringTool.Up to date');
    }

    /**
     * How many target languages of this source chapter still need a review, so the source language
     * list shows where translation work is left. Only the languages that are in use count: the
     * ones visible to learners and the ones whose translation has been started.
     */
    getChapterPendingTranslationLanguageCount (sourceChapter) {
        let languageCount = 0;

        this.lodash.forEach(this.trainingForm.materials, (material, index) => {
            const model = this.models[index];
            if (material.lang === this.sourceLanguage || !model) {
                return;
            }
            if (model.isEnabled !== true && this.getLanguageReadiness(index) === 'empty') {
                return;
            }

            const chapter = findChapterInModel(model, sourceChapter.hash);
            if (chapter && doesChapterNeedReview(chapter, sourceChapter, this.isSourceChanged(sourceChapter))) {
                languageCount++;
            }
        });

        return languageCount;
    }

    /**
     * Chapters of the selected target language that need review, in the order of the editor.
     */
    getChaptersNeedingReview () {
        return this.getLanguageChaptersNeedingReview(this.materialIndex);
    }

    /**
     * Chapters of a target language that need review, in the order of the editor.
     */
    getLanguageChaptersNeedingReview (materialIndex) {
        const material = this.trainingForm.materials[materialIndex];
        const model = this.models[materialIndex];
        const sourceModel = this.getSourceLanguageModel();
        if (!material || material.lang === this.sourceLanguage || !model || !sourceModel || model === sourceModel) {
            return [];
        }

        const sourceChaptersByKey = collectChaptersByHash(sourceModel);

        return this.lodash.filter(
            collectModelChapters(model),
            chapter => {
                const sourceChapter = sourceChaptersByKey[chapter.hash];

                return doesChapterNeedReview(chapter, sourceChapter, this.isSourceChanged(sourceChapter));
            }
        );
    }

    /**
     * Derived from the current chapters instead of a counter, so it can not drift from the editor.
     */
    getReviewRemainingCount () {
        return this.getChaptersNeedingReview().length;
    }

    /**
     * Review progress of a target language: how many chapters a learner of that language would get
     * (the hidden ones are left out), how many of them have no translation yet and how many have a
     * translation that still needs review.
     */
    getLanguageReviewSummary (materialIndex) {
        const sourceModel = this.getSourceLanguageModel();
        const sourceChaptersByKey = sourceModel ? collectChaptersByHash(sourceModel) : {};
        const summary = { total: 0, untranslated: 0, pending: 0, outdated: 0, review: 0 };

        this.lodash.forEach(collectModelChapters(this.models[materialIndex]), chapter => {
            if (chapter.isVisible === false) {
                return;
            }

            const sourceChapter = sourceChaptersByKey[chapter.hash];
            const state = getChapterTranslationState(chapter, sourceChapter, this.isSourceChanged(sourceChapter));
            summary.total++;
            if (state === 'missing') {
                summary.untranslated++;
            }
            else if (state === 'pending') {
                summary.pending++;
            }
            else if (state === 'outdated') {
                summary.outdated++;
            }
        });
        summary.review = summary.pending + summary.outdated;

        return summary;
    }

    /**
     * Readiness of a language for its learners: empty (nothing translated), progress (chapters
     * still missing or waiting for review) or ready.
     */
    getLanguageReadiness (materialIndex) {
        const material = this.trainingForm.materials[materialIndex];
        if (!material || material.lang === this.sourceLanguage) {
            return 'ready';
        }

        const summary = this.getLanguageReviewSummary(materialIndex);
        if (!summary.total || summary.untranslated === summary.total) {
            return 'empty';
        }

        return summary.untranslated + summary.review ? 'progress' : 'ready';
    }

    /**
     * The language option of the language selector: whether learners can choose the language and
     * how much translation work is left in it.
     */
    getLanguageOptionLabel (materialIndex) {
        const material = this.trainingForm.materials[materialIndex];
        const languageLabel = this.COMPANY_LANGUAGE_ENUM.LABELS[material.lang];

        const isVisible = this.isLanguageVisible(material.lang);

        if (material.lang === this.sourceLanguage) {
            if (!this.isAiTranslationModuleEnabled) {
                return `${isVisible ? '🟢' : '⚪'}  ${languageLabel}`;
            }

            return `🟢  ${translateWithLanguage(this.$translate, 'authoringTool.Language source', languageLabel)}`;
        }

        const summary = this.getLanguageReviewSummary(materialIndex);
        // one kind of open work is named, several kinds are only counted to keep the option short
        const openWork = [
            { key: 'authoringTool.Language chapters to translate', count: summary.untranslated },
            { key: 'authoringTool.Language chapters to review', count: summary.pending },
            { key: 'authoringTool.Language chapters source changed', count: summary.outdated }
        ].filter(work => work.count > 0);

        let state = '';
        if (!summary.total || summary.untranslated === summary.total) {
            state = this.$translate.instant('authoringTool.Language not started');
        }
        else if (openWork.length === 1) {
            state = this.$translate.instant(openWork[0].key, { count: openWork[0].count });
        }
        else if (openWork.length > 1) {
            state = this.$translate.instant('authoringTool.Language chapters need attention', {
                count: summary.untranslated + summary.review
            });
        }
        else if (!isVisible) {
            state = this.$translate.instant('authoringTool.Language ready');
        }

        return `${isVisible ? '🟢' : '⚪'}  ${languageLabel}${state ? ` · ${state}` : ''}`;
    }

    /**
     * Chapters of the selected target language a learner of that language would get, the hidden
     * ones are not counted.
     */
    getVisibleChapterCount () {
        return this.getLanguageReviewSummary(this.materialIndex).total;
    }

    /**
     * The learner visibility applies to a target language of an activity that has content. It does
     * not depend on the automatic translation setting, content that is already prepared can still be
     * shown to learners after automatic translations are turned off.
     */
    isVisibilityApplicable () {
        return !this.isSourceLanguageSelected() && this.hasChapters();
    }

    /**
     * An enabled language is visible to learners. It is never made visible by confirming the last
     * chapter, ready to be shown and visible are different states.
     */
    isSelectedLanguageVisible () {
        return this.isLanguageVisible(this.selectedLanguage);
    }

    /**
     * The language is visible and everything a learner gets is reviewed.
     */
    isLanguageLive () {
        return this.isVisibilityApplicable()
            && this.isSelectedLanguageVisible()
            && this.getReviewRemainingCount() === 0;
    }

    getLanguageLiveText () {
        return translateWithLanguage(
            this.$translate,
            'authoringTool.Learners can choose this language',
            this.COMPANY_LANGUAGE_ENUM.LABELS[this.selectedLanguage]
        );
    }

    /**
     * The visibility switch may only be switched on when the language is ready to be shown,
     * switching it off is always allowed for a visible language.
     */
    canToggleVisibility () {
        return this.isSelectedLanguageVisible()
            ? !this.visibilityLoading && !this.editorLoading
            : this.canShowSelectedLanguage();
    }

    toggleVisibility () {
        if (!this.canToggleVisibility()) {
            return;
        }

        if (this.isSelectedLanguageVisible()) {
            this.hideSelectedLanguage();
            return;
        }

        this.showSelectedLanguage();
    }

    /**
     * Chapters of a hidden language that have no translation yet, they keep it from being shown.
     */
    getHiddenUntranslatedCount () {
        if (!this.isVisibilityApplicable() || this.isSelectedLanguageVisible()) {
            return 0;
        }

        return this.getLanguageReviewSummary(this.materialIndex).untranslated;
    }

    /**
     * Translated chapters of a hidden language that still need review, they keep it from being shown.
     */
    getHiddenNeedingReviewCount () {
        if (!this.isVisibilityApplicable() || this.isSelectedLanguageVisible()) {
            return 0;
        }

        return this.getLanguageReviewSummary(this.materialIndex).review;
    }

    getUntranslatedBlockerText () {
        return this.$translate.instant('authoringTool.Chapters have no content in language', {
            count: this.getHiddenUntranslatedCount(),
            language: this.COMPANY_LANGUAGE_ENUM.LABELS[this.selectedLanguage]
        });
    }

    getReviewBlockerText () {
        return this.$translate.instant('authoringTool.Translations need review before language can be shown', {
            count: this.getHiddenNeedingReviewCount(),
            language: this.COMPANY_LANGUAGE_ENUM.LABELS[this.selectedLanguage]
        });
    }

    /**
     * Everything is reviewed, but showing the language still waits for the explicit decision of
     * the user.
     */
    isReadyToBeVisible () {
        return this.isVisibilityApplicable()
            && !this.isSelectedLanguageVisible()
            && this.getReviewRemainingCount() === 0;
    }

    /**
     * A visible language stays visible when its content later needs a review again, those
     * chapters are only kept hidden from learners until they are reviewed.
     */
    hasVisibleChaptersNeedingReview () {
        return this.isVisibilityApplicable()
            && this.isSelectedLanguageVisible()
            && this.getReviewRemainingCount() > 0;
    }

    getVisibleNotConfirmedText () {
        return this.$translate.instant('authoringTool.Chapters not confirmed and stay hidden', {
            count: this.getReviewRemainingCount()
        });
    }

    canShowSelectedLanguage () {
        return this.isReadyToBeVisible() && !this.visibilityLoading && !this.editorLoading;
    }

    showSelectedLanguage () {
        if (!this.canShowSelectedLanguage()) {
            return;
        }

        this.setSelectedLanguageVisible(true);
    }

    /**
     * Hiding a language takes it away from its learners, so it is confirmed first.
     */
    hideSelectedLanguage () {
        if (!this.isSelectedLanguageVisible() || this.visibilityLoading || this.editorLoading) {
            return;
        }

        const message = translateWithLanguage(
            this.$translate,
            'authoringTool.Hide language from learners confirmation',
            this.COMPANY_LANGUAGE_ENUM.LABELS[this.selectedLanguage]
        );

        this.ModalService.confirm(message, 'medium')
            .then(() => {
                this.setSelectedLanguageVisible(false);
            })
            .catch(() => {
                // user canceled — the language stays visible
            });
    }

    /**
     * Showing a language is an explicit backend operation: the editor state is saved first and the
     * backend recalculates from the stored content whether the language may be shown, so a stale
     * editor can not show a language that still has chapters needing review.
     *
     * An activity that is not saved yet has nothing the backend could check, there the editor
     * state is saved together with the activity.
     */
    setSelectedLanguageVisible (isVisible) {
        const language = this.selectedLanguage;
        const materialIndex = this.materialIndex;

        if (this.context !== 'edit' || !this.trainingForm.id) {
            this.applyLanguageVisibility(materialIndex, isVisible);
            return;
        }

        this.visibilityLoading = true;

        this.persistMaterials(false)
            .then(() => this.CourseTrainingResource.changeMaterialLanguageVisibility(
                { id: this.trainingForm.id },
                {
                    language,
                    isVisible
                }
            ).$promise)
            .then(response => {
                this.applyLanguageVisibility(materialIndex, response.isVisible === true);
                this.initialStateHash = this.getStateHash();
                this.toastr.success(this.$translate.instant(isVisible
                    ? 'authoringTool.The language is now visible to learners'
                    : 'authoringTool.The language is now hidden from learners'));
            })
            .catch(() => {
                // the backend refused it, the refreshed review state of the editor shows why
                this.toastr.error(this.$translate.instant(isVisible
                    ? 'authoringTool.Showing the language to learners failed'
                    : 'authoringTool.Hiding the language from learners failed'));
            })
            .finally(() => {
                this.visibilityLoading = false;
            });
    }

    /**
     * Takes the visibility over to the open editor, the language models are what is saved.
     */
    applyLanguageVisibility (materialIndex, isVisible) {
        const model = this.models[materialIndex];
        const material = this.trainingForm.materials[materialIndex];
        if (model) {
            model.isEnabled = isVisible;
        }
        if (material) {
            material.isEnabled = isVisible;
        }
    }

    isReviewNavigationAvailable () {
        return !this.isSourceLanguageSelected() && this.hasChapters();
    }

    getNextToReviewLabel () {
        return this.$translate.instant('authoringTool.Next to review with count', {
            count: this.getReviewRemainingCount()
        });
    }

    /**
     * Moves forward through the chapter order to the next chapter that needs review, continuing
     * from the beginning when the end of the list is reached.
     */
    goToNextChapterToReview () {
        const queue = this.getChaptersNeedingReview();
        if (!queue.length) {
            return;
        }

        const chapters = collectModelChapters(this.models[this.materialIndex]);
        const selected = this.getSelectedChapter();
        const selectedIndex = selected ? chapters.indexOf(selected) : -1;

        const next = this.lodash.find(queue, chapter => chapters.indexOf(chapter) > selectedIndex);

        this.setChapter(next || queue[0]);
    }

    /**
     * The review states only apply to the translations, the source language is written by hand.
     *
     * Reviewing is deliberately not tied to the automatic translation settings: an already
     * generated translation stays reviewable (and can still be shown to learners) after automatic
     * translations are switched off, exactly like the backend confirmation endpoint allows.
     */
    isSelectedLanguageTranslated () {
        return this.selectedLanguage !== this.sourceLanguage
            && !!this.getSelectedChapter();
    }

    isSelectedTranslationWaitingForReview () {
        const chapter = this.getSelectedChapter();

        return this.isSelectedLanguageTranslated() && chapter.isTranslationConfirmed === false;
    }

    isSelectedTranslationConfirmed () {
        const chapter = this.getSelectedChapter();

        return this.isSelectedLanguageTranslated()
            && chapter.isTranslationConfirmed !== false
            && !isTranslationOutdated(chapter, this.isSourceChanged(this.getSelectedSourceChapter()));
    }

    /**
     * The translation was confirmed, but the source language content has changed afterwards, so it
     * no longer describes the current source. The translated text is kept as it is, the reviewer
     * decides what to do with it.
     */
    isSelectedTranslationOutdated () {
        const chapter = this.getSelectedChapter();

        return this.isSelectedLanguageTranslated()
            && chapter.isTranslationConfirmed !== false
            && isTranslationOutdated(chapter, this.isSourceChanged(this.getSelectedSourceChapter()));
    }

    getTranslationOutdatedText () {
        return translateWithLanguage(
            this.$translate,
            'authoringTool.The source changed after this translation was confirmed',
            this.COMPANY_LANGUAGE_ENUM.LABELS[this.sourceLanguage]
        );
    }

    getTranslationReviewText () {
        const sourceLanguageLabel = this.COMPANY_LANGUAGE_ENUM.LABELS[this.sourceLanguage];

        return translateWithLanguage(
            this.$translate,
            'authoringTool.Automatic translation hidden from learners until confirmed',
            sourceLanguageLabel
        );
    }

    /**
     * Translations that were confirmed before the attribution was stored (and legacy content that
     * never needed a review) have no confirmer, those show the generic confirmed text.
     */
    getTranslationConfirmedText () {
        const chapter = this.getSelectedChapter();
        const confirmedByName = chapter ? chapter.translationConfirmedByName : null;
        const confirmedAt = formatTranslationConfirmedAt(chapter ? chapter.translationConfirmedAt : null);

        if (!confirmedByName || !confirmedAt) {
            return this.$translate.instant('authoringTool.Translation confirmed');
        }

        return this.$translate.instant('authoringTool.Translation confirmed by name at time', {
            name: confirmedByName,
            time: confirmedAt
        });
    }

    retranslateSelectedChapter () {
        const chapter = this.getSelectedChapter();
        if (!chapter) {
            return;
        }

        this.translateChapter(chapter);
    }

    /**
     * Confirming is an explicit backend operation on the stored translation, so the editor state is
     * saved first and the confirmed state is only shown once the backend has accepted it.
     */
    confirmSelectedTranslation () {
        const chapter = this.getSelectedChapter();
        if (!chapter || this.translationConfirmationLoading || this.editorLoading) {
            return;
        }

        const language = this.selectedLanguage;
        this.translationConfirmationLoading = true;

        this.persistMaterials(false)
            .then(() => this.CourseTrainingResource.confirmMaterialTranslation(
                { id: this.trainingForm.id },
                {
                    language,
                    chapterUniqueKey: chapter.hash
                }
            ).$promise)
            .then(response => {
                this.applyTranslationConfirmation(chapter, response);
                this.toastr.success(this.$translate.instant('authoringTool.The translation was confirmed'));
                // the review continues with the next chapter that still needs one
                this.goToNextChapterToReview();
            })
            .catch(() => {
                // the chapter stays in the review state so the user can try again
                this.toastr.error(this.$translate.instant('authoringTool.Confirming the translation failed'));
            })
            .finally(() => {
                this.translationConfirmationLoading = false;
            });
    }

    /**
     * Takes the review state the backend calculated for the saved material over to the open
     * editor, so a source language change makes its translations out of date right away instead
     * of only after the editor is opened again.
     *
     * Only the review state is taken over, the content stays the one the user is working on.
     */
    applyReviewStateFromMaterials (materials) {
        this.lodash.forEach(materials, material => {
            const materialIndex = this.lodash.findIndex(this.trainingForm.materials, { lang: material.lang });
            const model = this.models[materialIndex];
            if (!model) {
                return;
            }

            this.lodash.forEach(material.chapters, savedChapter => {
                const chapter = findChapterInModel(model, savedChapter.hash);
                if (chapter) {
                    chapter.isTranslationOutdated = savedChapter.isTranslationOutdated === true;
                    chapter.isTranslationSourceTracked = savedChapter.isTranslationSourceTracked === true;
                }
            });
        });
    }

    applyTranslationConfirmation (chapter, response) {
        chapter.isTranslationConfirmed = true;
        chapter.translationConfirmedByName = response ? response.translationConfirmedByName : null;
        chapter.translationConfirmedAt = response ? response.translationConfirmedAt : null;
        // the translation was just reviewed against the current source content
        chapter.isTranslationOutdated = false;
        chapter.isTranslationSourceTracked = true;
        this.initialStateHash = this.getStateHash();
    }

    selectLanguage (language) {
        this.selectedLanguage = language;
        this.changeLanguage();
    }

    changeLanguage () {
        this.setMaterial();
        this.setActiveChapter();
        this.setDynamicTranslations(this.selectedLanguage);
    }

    addDefaultMaterials () {
        if (this.trainingForm.materials === undefined) {
            this.trainingForm.materials = [];
        }
        this.lodash.forEach(this.companySupportedLanguages, lang => {
            const material = this.trainingForm.materials.find(m => m.lang === lang);
            if (!material) {
                this.addMaterial(lang, false);
            }
        });
    }

    addMaterial (lang, isEnabled) {
        const material = {
            trainingId: this.trainingForm.id,
            isEnabled,
            lang,
            chapters: []
        };
        this.trainingForm.materials.push(material);
    }

    addSection () {
        this.setMaterial();

        const sectionName = this.$translate.instant('authoringTool.New section');
        const uniqueKey = createUniqueKey();

        this.lodash.forEach(this.models, (model) => {
            if (model === undefined) {
                return;
            }
            let section = {
                name: sectionName,
                uniqueKey
            };
            section = this.initItem(section, 'section');
            model.list.push(section);
        });

        this.showAddedIntoAllLanguagesToast('authoringTool.Section added into all languages');
    }

    showAddedIntoAllLanguagesToast (translationKey) {
        // shown next to the chapter list on the left, where the user is working
        this.toastr.success(this.$translate.instant(translationKey), '', {
            toastClass: 'toast authoring-tool-toast-left'
        });
    }

    toggleChapterVisibility (chapter) {
        chapter.isVisible = !chapter.isVisible;
        this.closeMenus();
        this.actions = false;
    }

    /**
     * Applies the given visibility of the chapter to every language of the material, so the
     * chapter does not have to be shown or hidden language by language.
     */
    setChapterVisibilityInAllLanguages (chapter, isVisible) {
        this.lodash.forEach(this.models, (model) => {
            if (model === undefined) {
                return;
            }
            this.lodash.forEach(model.list, item => {
                if (item === undefined) {
                    return;
                }
                if (item.type === 'chapter' && isSameChapter(item, chapter)) {
                    item.isVisible = isVisible;
                }
                else if (item.type === 'section') {
                    this.lodash.forEach(item.items, subitem => {
                        if (subitem !== undefined && isSameChapter(subitem, chapter)) {
                            subitem.isVisible = isVisible;
                        }
                    });
                }
            });
        });

        this.closeMenus();
        this.actions = false;
    }

    /**
     * Sections themselves are not hideable, hiding or showing a section means applying the
     * visibility to all of its chapters, in every language.
     */
    setSectionChaptersVisibility (section, isVisible) {
        this.lodash.forEach(section.items, chapter => {
            this.setChapterVisibilityInAllLanguages(chapter, isVisible);
        });

        this.closeMenus();
        this.actions = false;
    }

    /**
     * Lets the user copy the content of the chapter to the same chapter of other languages, so
     * translating can be started from the already written content instead of an empty chapter.
     */
    exportChapter (chapter) {
        this.closeMenus();
        this.actions = false;

        const languages = this.lodash.reduce(this.trainingForm.materials, (result, material, index) => {
            if (material.lang !== this.selectedLanguage && this.models[index] !== undefined) {
                result.push({
                    lang: material.lang,
                    label: this.COMPANY_LANGUAGE_ENUM.LABELS[material.lang],
                    selected: false
                });
            }
            return result;
        }, []);

        if (!languages.length) {
            return;
        }

        this.openExportChapterModal(chapter, languages);
    }

    /**
     * The language selection is reopened with the previous selections whenever the user decides
     * not to overwrite the existing content, so the selection can be corrected.
     */
    openExportChapterModal (chapter, languages) {
        this.ModalService.open({
            size: 'medium',
            template: `
                <modals-courses-authoring-tool-export-chapter
                    languages="vm.languages"
                    ok="vm.$uibModalInstance.close()"
                    cancel="vm.$uibModalInstance.dismiss()"></modals-courses-authoring-tool-export-chapter>
            `,
            controller ($uibModalInstance) {
                this.$uibModalInstance = $uibModalInstance;
                this.languages = languages;
            }
        }).then(() => {
            const selectedLanguages = this.lodash.filter(languages, { selected: true });
            const languagesWithContent = this.lodash.filter(selectedLanguages, language => {
                return this.hasChapterContentInLanguage(chapter, language.lang);
            });

            if (!languagesWithContent.length) {
                this.copyChapterToLanguages(chapter, selectedLanguages);
                return;
            }

            const message = this.$translate.instant(
                'authoringTool.The following language(s) already have content in this chapter, are you sure you want to overwrite them? {languages}',
                { languages: this.lodash.map(languagesWithContent, 'label').join(', ') }
            );

            this.ModalService.confirm(message, 'medium')
                .then(() => {
                    this.copyChapterToLanguages(chapter, selectedLanguages);
                })
                .catch(() => {
                    this.openExportChapterModal(chapter, languages);
                });
        }).catch(() => {
            // user canceled — do nothing
        });
    }

    /**
     * Exporting overwrites the content of the target chapter, so the languages that would lose
     * their existing content have to be found before copying.
     */
    hasChapterContentInLanguage (chapter, lang) {
        const modelIndex = this.trainingForm.materials.findIndex(material => material.lang === lang);
        const model = this.models[modelIndex];
        if (model === undefined || !chapter.hash) {
            return false;
        }

        const targetChapter = collectChaptersByHash(model)[chapter.hash];
        return Boolean(targetChapter && targetChapter.content);
    }

    /**
     * The uploaded files and images are part of the chapter content, so copying the name and the
     * content is enough to have the whole chapter in the target language.
     */
    copyChapterToLanguages (chapter, languages) {
        this.lodash.forEach(languages, language => {
            const modelIndex = this.trainingForm.materials.findIndex(material => material.lang === language.lang);
            const model = this.models[modelIndex];
            if (model === undefined) {
                return;
            }

            const targetChapter = collectChaptersByHash(model)[chapter.hash];
            if (!targetChapter) {
                return;
            }

            targetChapter.name = chapter.name;
            targetChapter.content = chapter.content;

            this.toastr.success(this.$translate.instant(
                'authoringTool.Chapter content was copied to {language}',
                { language: language.label }
            ));
        });
    }

    deleteSection (section) {
        const performDelete = () => {
            this.lodash.forEach(this.models, (m) => {
                if (m === undefined) {
                    return;
                }
                this.lodash.remove(m.list, item => item.uniqueKey === section.uniqueKey);
            });
            this.setActiveChapter();
        };

        if (!this.hasSectionContentInAnyLanguage(section)) {
            performDelete();
        }
        else {
            const message = this.$translate.instant(
                'authoringTool.Section will be removed along with all its chapters and their translations. Are you sure you want to delete the section?'
            );
            this.ModalService.confirm(message, 'medium')
                .then(() => {
                    performDelete();
                })
                .catch(() => {
                    // user canceled — do nothing
                });
        }
    }

    deleteChapter (chapter) {
        const performDelete = () => {
            this.lodash.forEach(this.models, (m) => {
                if (m === undefined) {
                    return;
                }
                this.lodash.remove(m.list, item => item.type === 'chapter' && isSameChapter(item, chapter));
                this.lodash.forEach(m.list, item => {
                    if (item !== undefined && item.type === 'section') {
                        this.lodash.remove(item.items, itemToDelete => isSameChapter(itemToDelete, chapter));
                    }
                });
            });
            this.setActiveChapter();
        };

        if (!this.hasChapterContentInAnyLanguage(chapter)) {
            performDelete();
        }
        else {
            const message = this.$translate.instant(
                'authoringTool.Chapter will be removed along with all its translations. Are you sure you want to delete the chapter?'
            );
            this.ModalService.confirm(message, 'medium')
                .then(() => {
                    performDelete();
                })
                .catch(() => {
                    // user canceled — do nothing
                });
        }
    }

    /**
     * The content is language specific, so the deletion has to be confirmed whenever any of the
     * translations of the chapter has content, not only the language currently in focus.
     */
    hasChapterContentInAnyLanguage (chapter) {
        if (!chapter.hash) {
            return Boolean(chapter.content);
        }

        return this.lodash.some(this.models, model => {
            if (model === undefined) {
                return false;
            }
            const chapterOfLanguage = collectChaptersByHash(model)[chapter.hash];
            return Boolean(chapterOfLanguage && chapterOfLanguage.content);
        });
    }

    /**
     * A section is deleted along with its chapters, so the confirmation is needed whenever any
     * chapter of the section has content in any of the languages.
     */
    hasSectionContentInAnyLanguage (section) {
        return this.lodash.some(section.items, chapter => this.hasChapterContentInAnyLanguage(chapter));
    }

    addChapterToSection (section) {
        this.addChapterIntoAllLanguages((model, chapter) => {
            const targetSection = findSectionByUniqueKey(model, section.uniqueKey);
            if (targetSection) {
                targetSection.items.push(chapter);
            }
        });
    }

    addChapter () {
        this.setMaterial();
        this.addChapterIntoAllLanguages((model, chapter) => {
            model.list.push(chapter);
        });
    }

    /**
     * The structure is language neutral, so a new chapter is added into every language with the
     * same hash. It is opened in the selected language.
     *
     * @param placeChapter puts the chapter of a language into the structure of that language
     */
    addChapterIntoAllLanguages (placeChapter) {
        const chapterName = this.$translate.instant('course.materialSource.Chapter');
        const hash = createChapterHash();
        const chapterNumber = collectModelChapters(this.models[this.materialIndex]).length + 1;
        const uniqueKey = createUniqueKey();

        this.lodash.forEach(this.models, (model, modelIndex) => {
            if (model === undefined) {
                return;
            }
            const chapter = this.initItem({
                hash,
                name: `${chapterName} ${chapterNumber}`,
                uniqueKey
            }, 'chapter');
            placeChapter(model, chapter);

            if (modelIndex === this.materialIndex) {
                model.selected = chapter;
                this.chapterUniqueKeysForGeneratedTinyMce.push(chapter.uniqueKey);
            }
        });

        this.showAddedIntoAllLanguagesToast('authoringTool.Chapter added into all languages');
    }

    onChapterMoved (sourceList, index) {
        sourceList.splice(index, 1);
        this.syncStructureAcrossLanguages();
    }

    syncStructureAcrossLanguages () {
        const activeModel = this.models[this.materialIndex];

        this.lodash.forEach(this.models, (model, modelIndex) => {
            if (modelIndex === this.materialIndex) {
                return;
            }

            // the chapters have to be collected up front: rebuilding the list empties the sections
            // and a chapter moved out of a section but placed after it would no longer be findable
            const chaptersByHash = collectChaptersByHash(model);

            const newList = [];
            this.lodash.forEach(activeModel.list, (activeItem) => {
                if (activeItem.type === 'chapter') {
                    const chapter = chaptersByHash[activeItem.hash];
                    if (chapter) {
                        newList.push(chapter);
                    }
                }
                else if (activeItem.type === 'section') {
                    let section = findSectionByUniqueKey(model, activeItem.uniqueKey);
                    if (!section) {
                        section = this.lodash.clone(activeItem);
                        section.items = [];
                        section = this.initItem(section, 'section');
                    }
                    const newItems = [];
                    this.lodash.forEach(activeItem.items, (activeChapter) => {
                        const chapter = chaptersByHash[activeChapter.hash];
                        if (chapter) {
                            newItems.push(chapter);
                        }
                    });
                    section.items = newItems;
                    newList.push(section);
                }
            });
            model.list = newList;
        });
    }

    /**
     * Content is added to every language at once, the source language it is written in becomes
     * visible to learners with it. A target language is only shown on purpose, once it is reviewed.
     */
    enableMaterial () {
        const sourceModel = this.getSourceLanguageModel();
        if (sourceModel) {
            sourceModel.isEnabled = true;
        }
    }

    /**
     * The source language is always visible to learners, it is the authoritative content of the
     * activity. A source language that has no content yet (for example a legacy activity written in
     * another language) is left as it is, learners would only get empty chapters.
     */
    keepSourceLanguageVisible () {
        const sourceModel = this.getSourceLanguageModel();
        if (!sourceModel) {
            return;
        }

        const hasContent = this.lodash.some(
            collectModelChapters(sourceModel),
            chapter => !!chapter.content && chapter.content.trim().length > 0
        );
        if (hasContent) {
            sourceModel.isEnabled = true;
        }
    }

    handleOutsideMenusClicks () {
        // handle closing menu dropdowns
        const documentClickHandler = (event) => {
            if (!event.target.closest('.material-editor-02__action-menu')) {
                this.closeMenus();
                this.actions = false;
            }
        };

        this.$document.on('click', documentClickHandler);

        // handle closing menu edit mode
        document.addEventListener('mousedown', (event) => {
            const clickedElement = event.target;
            if (!clickedElement.classList.contains('menu-item-edit-input')) {
                this.removeMenuEditMode();
            }
        });
    }

    toggleSettings () {
        if (this.settings) {
            this.closeSettings();
            return;
        }
        this.settings = true;
        this.toggleEditorsClickListener(true);
    }

    closeSettings () {
        this.settings = false;
        this.toggleEditorsClickListener(false);
    }

    /**
     * Content editors live in iframes, so their clicks never reach the document listener.
     * Each TinyMCE instance is listened directly while the settings panel is open.
     */
    toggleEditorsClickListener (isListening) {
        if (!window.tinymce) {
            return;
        }
        window.tinymce.get().forEach((editor) => {
            if (isListening) {
                editor.on('mousedown', this.editorClickHandler);
            }
            else {
                editor.off('mousedown', this.editorClickHandler);
            }
        });
    }

    handleSettingsPanelClosing () {
        this.editorClickHandler = () => this.$scope.$evalAsync(() => this.closeSettings());

        const outsideClickHandler = (event) => {
            const target = event.target;
            if (!this.settings || !document.contains(target)) {
                return;
            }
            // the toggle button handles opening and closing by itself
            if (target.closest('.material-editor-02__settings, .material-editor-02__settings-toggle')) {
                return;
            }
            this.$scope.$evalAsync(() => this.closeSettings());
        };

        const escapeKeyHandler = (event) => {
            if (!this.settings || event.key !== 'Escape') {
                return;
            }
            // listened in capture phase and default prevented, so the uib modal is not dismissed as well
            event.preventDefault();
            event.stopPropagation();
            this.$scope.$apply(() => this.closeSettings());
        };

        // capture phase, as several elements (drag and drop list, editor toolbar) stop click propagation
        document.addEventListener('click', outsideClickHandler, true);
        document.addEventListener('keydown', escapeKeyHandler, true);

        this.$scope.$on('$destroy', () => {
            this.toggleEditorsClickListener(false);
            document.removeEventListener('click', outsideClickHandler, true);
            document.removeEventListener('keydown', escapeKeyHandler, true);
        });
    }

    setActiveChapter () {
        const model = this.models[this.materialIndex];
        for (let i = 0; i < model.list.length; i++) {
            const item = model.list[i];
            if (item.type === 'section') {
                for (let j = 0; j < item.items.length; j++) {
                    const chapter = item.items[j];
                    model.selected = chapter;
                    this.chapterUniqueKeysForGeneratedTinyMce.push(chapter.uniqueKey);
                    return;
                }
            }
            model.selected = item;
            this.chapterUniqueKeysForGeneratedTinyMce.push(item.uniqueKey);
            return;
        }
    }

    getDefaultSelectedLanguage () {
        let language = this.defaultLanguage;
        const material = this.lodash.find(
            this.trainingForm.materials,
            item => item.isEnabled && this.isCompanyLanguage(item.lang)
        );
        if (material) {
            language = material.lang;
        }
        return language;
    }

    // materials of a language the company no longer offers stay saved, but are not shown
    isCompanyLanguage (lang) {
        return this.lodash.includes(this.companySupportedLanguages, lang);
    }

    isLanguageVisible (lang) {
        const materialIndex = this.lodash.findIndex(this.trainingForm.materials, material => material.lang === lang);
        return this.models[materialIndex] !== undefined ? this.models[materialIndex].isEnabled === true : false;
    }

    canTranslateInto (lang) {
        return this.isAiTranslationAvailable
            && this.automaticTranslationsEnabled === true
            && this.sourceLanguage !== lang
            && this.context === 'edit'
            && !!this.trainingForm.id;
    }

    translateLanguage (lang, materialIndex) {
        this.editorLoading = true;

        const sourceModel = this.getSourceLanguageModel();

        this.CourseTrainingResource.translateMaterial(
            { id: this.trainingForm.id },
            {
                targetLanguage: lang,
                chapterUniqueKeys: null,
                // the editor state is sent so unsaved chapters and edits are translated as well
                sourceChapters: buildSourceChapters(sourceModel),
                sourceSections: buildSourceSections(sourceModel),
                // the settings may have been changed without saving, they are used instead of the stored ones
                automaticTranslationsEnabled: this.automaticTranslationsEnabled === true,
                sourceLanguage: this.sourceLanguage
            }
        ).$promise
            .then(response => {
                this.applyTranslations(materialIndex, response.chapters, response.sections);
                this.showTranslationResult(lang, response);
            })
            .catch(() => {
                this.toastr.error(this.$translate.instant('authoringTool.Translating the content failed'));
            })
            .finally(() => {
                this.editorLoading = false;
            });
    }

    canTranslateSelectedChapter () {
        return this.canTranslateInto(this.selectedLanguage);
    }

    getChapterTranslationLabel () {
        const sourceLanguageLabel = this.COMPANY_LANGUAGE_ENUM.LABELS[this.sourceLanguage];

        return translateWithLanguage(
            this.$translate,
            'authoringTool.Translate chapter from language',
            sourceLanguageLabel
        );
    }

    translateChapter (chapter) {
        if (!this.canTranslateSelectedChapter()) {
            return;
        }

        this.closeMenus();
        this.actions = false;

        const message = this.$translate.instant('authoringTool.The current translation of this chapter will be overwritten');
        this.ModalService.confirm(message, 'medium')
            .then(() => {
                this.translateSingleChapter(chapter);
            })
            .catch(() => {
                // user canceled — the existing translation is kept
            });
    }

    translateSingleChapter (chapter) {
        const targetLanguage = this.selectedLanguage;
        const materialIndex = this.materialIndex;
        const sourceModel = this.getSourceLanguageModel();

        this.editorLoading = true;

        this.CourseTrainingResource.translateMaterial(
            { id: this.trainingForm.id },
            {
                targetLanguage,
                chapterUniqueKeys: [chapter.hash],
                // the editor state is sent so unsaved changes of the source language are translated as well
                sourceChapters: buildSourceChapters(sourceModel),
                // no section names are translated when a single chapter is requested
                sourceSections: [],
                // the settings may have been changed without saving, they are used instead of the stored ones
                automaticTranslationsEnabled: this.automaticTranslationsEnabled === true,
                sourceLanguage: this.sourceLanguage
            }
        ).$promise
            .then(response => {
                const translatedChapters = response.chapters ? response.chapters : [];
                if (!translatedChapters.length) {
                    this.toastr.error(this.$translate.instant('authoringTool.Translating the content failed'));
                    return;
                }

                this.applyChapterTranslation(materialIndex, translatedChapters[0]);

                const languageLabel = this.COMPANY_LANGUAGE_ENUM.LABELS[targetLanguage];
                this.toastr.success(translateWithLanguage(
                    this.$translate,
                    'authoringTool.Chapter translated into language',
                    languageLabel
                ));
            })
            .catch(() => {
                this.toastr.error(this.$translate.instant('authoringTool.Translating the content failed'));
            })
            .finally(() => {
                this.editorLoading = false;
            });
    }

    applyChapterTranslation (materialIndex, translatedChapter) {
        const model = this.models[materialIndex];
        if (!model) {
            return;
        }

        const chapter = findChapterInModel(model, translatedChapter.uniqueKey);
        if (!chapter) {
            return;
        }

        chapter.name = translatedChapter.name;
        chapter.content = translatedChapter.content;
        markChapterTranslationForReview(chapter);

        // a TinyMCE instance that does not exist yet picks the new content up when it is created
        if (!this.isTinyMceGeneratedForChapter(chapter)) {
            this.chapterUniqueKeysForGeneratedTinyMce.push(chapter.uniqueKey);
            return;
        }

        this.$rootScope.$broadcast('tinymce:setContent', {
            uniqueKey: chapter.uniqueKey,
            hash: chapter.hash,
            content: translatedChapter.content
        });
    }

    canTranslateAllChapters () {
        const model = this.models[this.materialIndex];

        return this.canTranslateInto(this.selectedLanguage)
            && !!model
            && !!model.list
            && model.list.length > 0;
    }

    getTranslateAllChaptersLabel () {
        const untranslatedChapterCount = this.getUntranslatedChapterCount();
        if (!untranslatedChapterCount) {
            return this.$translate.instant('authoringTool.Translate all chapters');
        }

        return this.$translate.instant('authoringTool.Translate all chapters with missing count', {
            count: untranslatedChapterCount
        });
    }

    /**
     * Counts the chapters of the selected language that have no content yet or still hold the
     * untranslated source language content.
     */
    getUntranslatedChapterCount () {
        const model = this.models[this.materialIndex];
        const sourceModel = this.getSourceLanguageModel();
        if (!model || !sourceModel || model === sourceModel) {
            return 0;
        }

        const sourceChaptersByKey = collectChaptersByHash(sourceModel);

        return this.lodash.filter(
            collectModelChapters(model),
            chapter => isChapterUntranslated(chapter, sourceChaptersByKey[chapter.hash])
        ).length;
    }

    translateAllChapters () {
        if (this.editorLoading || !this.canTranslateAllChapters()) {
            return;
        }

        const targetLanguage = this.selectedLanguage;
        const materialIndex = this.materialIndex;
        const message = this.$translate.instant('authoringTool.Every chapter of this language will be retranslated and existing work may be lost');

        this.ModalService.confirm(message, 'medium')
            .then(() => {
                this.translateLanguage(targetLanguage, materialIndex);
            })
            .catch(() => {
                // user canceled — the existing translations are kept
            });
    }

    isTinyMceGeneratedForChapter (chapter) {
        return Boolean(chapter) && this.chapterUniqueKeysForGeneratedTinyMce.indexOf(chapter.uniqueKey) !== -1;
    }

    getSourceLanguageModel () {
        const sourceIndex = this.lodash.findIndex(
            this.trainingForm.materials,
            material => material.lang === this.sourceLanguage
        );

        return sourceIndex === -1 ? null : this.models[sourceIndex];
    }

    applyTranslations (materialIndex, chapters, sections) {
        const model = this.models[materialIndex];
        if (!model) {
            return;
        }

        this.lodash.forEach(chapters, translatedChapter => {
            const chapter = findChapterInModel(model, translatedChapter.uniqueKey);
            if (chapter) {
                chapter.name = translatedChapter.name;
                chapter.content = translatedChapter.content;
                markChapterTranslationForReview(chapter);
                // the TinyMCE instance keeps its own copy of the content, it has to be updated separately
                this.$rootScope.$broadcast('tinymce:setContent', {
                    uniqueKey: chapter.uniqueKey,
                    hash: chapter.hash,
                    content: translatedChapter.content
                });
            }
        });

        this.lodash.forEach(sections, translatedSection => {
            const section = findSectionForTranslation(model, translatedSection);
            if (section) {
                section.name = translatedSection.name;
            }
        });
    }

    showTranslationResult (lang, response) {
        const languageLabel = this.COMPANY_LANGUAGE_ENUM.LABELS[lang];
        const failedChapters = response.failedChapters ? response.failedChapters : [];
        const translatedChapters = response.chapters ? response.chapters : [];

        if (!translatedChapters.length) {
            this.toastr.error(this.$translate.instant('authoringTool.Translating the content failed'));
            return;
        }

        if (failedChapters.length) {
            this.toastr.warning(this.$translate.instant('authoringTool.Some chapters could not be translated', {
                count: failedChapters.length
            }));
            return;
        }

        this.toastr.success(translateWithLanguage(
            this.$translate,
            'authoringTool.Content translated into language',
            languageLabel
        ));
    }

    setMaterial () {
        this.materialIndex = this.trainingForm.materials.findIndex(m => m.lang === this.selectedLanguage);
        // this.material = this.trainingForm.materials[this.materialIndex];

        if (this.models[this.materialIndex] === undefined) {
            this.models[this.materialIndex] = {
                list: []
            };
        }
    }

    /**
     * The settings are not part of the models, they are added so that changing only a setting counts as unsaved
     */
    getStateHash () {
        return JSON.stringify({
            models: this.models,
            chapterNavigation: this.chapterNavigation,
            automaticTranslationsEnabled: this.automaticTranslationsEnabled,
            sourceLanguage: this.sourceLanguage
        });
    }

    closeModal () {
        const confirmMessage = this.$translate.instant('general.Latest changes have not been saved. Do you want to proceed editing to save changes?');

        // wait until content from tinyMce will be updated
        setTimeout(() => {
            const stateHash = this.getStateHash();

            if (this.initialStateHash === stateHash) {
                this.$rootScope.$broadcast('material:init');
                this.uibModalInstance.close();
            }
            else {
                this.ModalService.confirm(confirmMessage, 'medium')
                .catch((action) => {
                    if (action === 'no') {
                        this.$rootScope.$broadcast('material:init');
                        this.uibModalInstance.close();
                    }
                });
            }
        }, 200);
    }

    save () {
        this.persistMaterials();
    }

    /**
     * Saves the editor state and resolves once the material has been persisted, so operations that
     * work on the stored content (for example confirming a translation) can be chained onto it.
     *
     * @param showSuccessMessage whether the generic "changes saved" message is shown
     */
    persistMaterials (showSuccessMessage = true) {
        this.errors = [];
        this.editorLoading = true;
        this.convertModelsToMaterials();

        return this.$q((resolve, reject) => {
            // wait until content from tinyMce will be updated
            setTimeout(() => {
                if (this.context !== 'edit') {
                    this.initialStateHash = this.getStateHash();
                    this.editorLoading = false;
                    resolve();
                    return;
                }

                this.CourseTrainingResource.updateMaterials({ id: this.trainingForm.id, expand:
                    'chapters.chapterI18ns,chapters.section.sectionI18ns,languages'
                 }, { materials: this.trainingForm.materials }).$promise
                    .then(response => {
                        delete response.$promise;
                        delete response.$resolved;

                        if (showSuccessMessage) {
                            this.toastr.success(this.$translate.instant('general.Changes saved'));
                        }

                        // the models of the editor are kept, so the materials have to stay
                        // in the same language order as they were before the save
                        const languageOrder = this.lodash.map(this.trainingForm.materials, 'lang');

                        this.trainingForm.materials = this.MaterialService.sortMaterialsByLanguages(
                            this.MaterialService.transformMaterialsFromApi([response]),
                            languageOrder
                        );
                        this.addDefaultMaterials();
                        this.applyReviewStateFromMaterials(this.trainingForm.materials);
                        this.rememberSavedContents();
                        resolve(response);
                    }).catch((response) => {
                        this.toastr.error(this.$translate.instant('general.There were errors processing the form'));
                        if (response && response.data) {
                            response.data.forEach((error) => {
                                this.errors.push(error.message);
                            });
                        }
                        reject(response);
                    }).finally(() => {
                        this.initialStateHash = this.getStateHash();
                        this.editorLoading = false;
                    });
            }, 200);
        });
    }

}



// WEBPACK FOOTER //
// ./src/app/components/modals/courses/authoringTool/editor/index.js